import React, { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Card,
  CardContent,
  CardHeader,
  Button,
  Chip,
  Stack,
  Avatar,
  Alert,
  List,
  ListItem,
  ListItemText,
  ListItemAvatar,
  ListItemSecondaryAction,
  Divider,
  Paper,
  CircularProgress,
  Badge
} from '@mui/material';
import Grid from '@mui/material/Grid';
import {
  CalendarToday as CalendarIcon,
  Assignment as TaskIcon,
  Message as MessageIcon,
  Science as LabIcon,
  Pets as PetsIcon,
  VideoCall as VideoIcon,
  Warning as WarningIcon,
  TrendingUp as TrendingUpIcon,
  Refresh as RefreshIcon,
  LocalHospital as HospitalIcon
} from '@mui/icons-material';
import { useAuth } from '@contexts/AuthContext';
import { useNavigate } from 'react-router-dom';
import { doctorDashboardService } from '@services/doctorDashboardService';
import { formatForDisplay, formatDateOnly } from '@utils/dateFormatter';

const DoctorDashboard: React.FC = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [dashboardData, setDashboardData] = useState<any>(null);

  useEffect(() => {
    loadDashboardData();
  }, []);

  const loadDashboardData = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await doctorDashboardService.getDashboardData(new Date().toISOString().split('T')[0]);
      setDashboardData(data);
    } catch (err: any) {
      console.error('Eroare incarcare dashboard:', err);
      setError('Eroare la incarcarea datelor dashboard-ului');
    } finally {
      setLoading(false);
    }
  };

  const getStatusColor = (status: string) => {
    switch (status) {
      case 'scheduled': return 'info';
      case 'confirmed': return 'success';
      case 'in_progress': return 'warning';
      case 'completed': return 'success';
      case 'no_show': return 'error';
      default: return 'default';
    }
  };

  const getStatusLabel = (status: string) => {
    const labels: any = {
      scheduled: 'Programat',
      confirmed: 'Confirmat',
      in_progress: 'In progres',
      completed: 'Finalizat',
      no_show: 'Absent',
      cancelled: 'Anulat'
    };
    return labels[status] || status;
  };

  const getPriorityColor = (priority: string) => {
    switch (priority) {
      case 'urgent': return 'error';
      case 'high': return 'warning';
      case 'medium': return 'info';
      case 'low': return 'success';
      default: return 'default';
    }
  };

  const getAlertSeverity = (type: string) => {
    switch (type) {
      case 'critical_lab_value': return 'error';
      case 'expired_consent': return 'warning';
      default: return 'info';
    }
  };

  if (loading) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
        <CircularProgress />
        <Typography variant="body2" sx={{ ml: 2 }}>
          Se incarca dashboard-ul...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box sx={{ p: 3 }}>
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
        <Button variant="contained" onClick={loadDashboardData}>
          Incearca din nou
        </Button>
      </Box>
    );
  }

  return (
    <Box sx={{ p: 3 }}>
      {/* Header */}
      <Box display="flex" justifyContent="space-between" alignItems="center" mb={3}>
        <Box>
          <Typography variant="h4" component="h1">
            Buna ziua, Dr. {user?.lastName}!
          </Typography>
          <Typography variant="body2" color="text.secondary">
            {new Date().toLocaleDateString('ro-RO', { weekday: 'long', year: 'numeric', month: 'long', day: 'numeric' })}
          </Typography>
        </Box>
        <Stack direction="row" spacing={2}>
          <Button
            variant="outlined"
            startIcon={<RefreshIcon />}
            onClick={loadDashboardData}
          >
            Actualizeaza
          </Button>
        </Stack>
      </Box>

      {/* KPIs Row */}
      <Grid container spacing={2} sx={{ mb: 3 }}>
        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" color="primary.main">
                    {dashboardData?.kpis.consultations_today || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Consultatii azi
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'primary.main' }}>
                  <HospitalIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" color="success.main">
                    {dashboardData?.kpis.no_show_rate ? dashboardData.kpis.no_show_rate.toFixed(1) : '0.0'}%
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Rata no-show
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'success.main' }}>
                  <TrendingUpIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" color="info.main">
                    {Math.round(dashboardData?.kpis.avg_consultation_time || 0)} min
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Timp mediu consultatie
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'info.main' }}>
                  <CalendarIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>

        <Grid size={{ xs: 12, sm: 6, md: 3 }}>
          <Card>
            <CardContent>
              <Box display="flex" justifyContent="space-between" alignItems="center">
                <Box>
                  <Typography variant="h4" color="warning.main">
                    {dashboardData?.unread_messages || 0}
                  </Typography>
                  <Typography variant="body2" color="text.secondary">
                    Mesaje necitite
                  </Typography>
                </Box>
                <Avatar sx={{ bgcolor: 'warning.main' }}>
                  <MessageIcon />
                </Avatar>
              </Box>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Main Grid */}
      <Grid container spacing={3}>
        {/* Left Column */}
        <Grid size={{ xs: 12, lg: 8 }}>
          <Stack spacing={3}>
            {/* Alerte clinice */}
            {dashboardData?.clinical_alerts && dashboardData.clinical_alerts.length > 0 && (
              <Card>
                <CardHeader 
                  title="Alerte Clinice" 
                  avatar={
                    <Badge badgeContent={dashboardData.clinical_alerts.length} color="error">
                      <WarningIcon color="error" />
                    </Badge>
                  }
                />
                <CardContent>
                  <Stack spacing={2}>
                    {dashboardData.clinical_alerts.map((alert: any, index: number) => (
                      <Alert key={index} severity={getAlertSeverity(alert.type)}>
                        {alert.message}
                      </Alert>
                    ))}
                  </Stack>
                </CardContent>
              </Card>
            )}

            {/* Agenda de azi */}
            <Card>
              <CardHeader 
                title="Agenda de Azi" 
                action={
                  <Badge badgeContent={dashboardData?.today_agenda?.length || 0} color="primary">
                    <CalendarIcon />
                  </Badge>
                }
              />
              <CardContent>
                {!dashboardData?.today_agenda || dashboardData.today_agenda.length === 0 ? (
                  <Alert severity="info">
                    Nu aveti programari pentru astazi.
                  </Alert>
                ) : (
                  <List>
                    {dashboardData.today_agenda.map((apt: any, index: number) => (
                      <React.Fragment key={`apt-${apt.id}-${index}`}>
                        <ListItem>
                          <ListItemAvatar>
                            <Avatar sx={{ bgcolor: apt.emergency ? 'error.main' : 'primary.main' }}>
                              <PetsIcon />
                            </Avatar>
                          </ListItemAvatar>
                          <ListItemText
                            primary={
                              <Box display="flex" alignItems="center" gap={1}>
                                <Typography variant="subtitle1">
                                  {new Date(apt.scheduledAt).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                                </Typography>
                                {apt.emergency && (
                                  <Chip label="URGENT" color="error" size="small" />
                                )}
                              </Box>
                            }
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  Pacient: {apt.patient?.name || 'N/A'}
                                </Typography>
                                <Typography variant="body2" color="text.secondary">
                                  Motiv: {apt.reason}
                                </Typography>
                              </Box>
                            }
                          />
                          <ListItemSecondaryAction>
                            <Chip
                              label={getStatusLabel(apt.status)}
                              color={getStatusColor(apt.status)}
                              size="small"
                            />
                          </ListItemSecondaryAction>
                        </ListItem>
                        {index < dashboardData.today_agenda.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
                <Box textAlign="center" pt={2}>
                  <Button variant="outlined" onClick={() => navigate('/doctor/appointments')}>
                    Vezi toate programarile
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Rezultate Lab recente */}
            <Card>
              <CardHeader 
                title="Rezultate Lab Recente" 
                subheader="Ultimele 48 ore"
                avatar={<LabIcon color="primary" />}
              />
              <CardContent>
                {!dashboardData?.recent_lab_results || dashboardData.recent_lab_results.length === 0 ? (
                  <Alert severity="info">
                    Nu exista rezultate lab noi.
                  </Alert>
                ) : (
                  <List>
                    {dashboardData.recent_lab_results.map((result: any, index: number) => (
                      <React.Fragment key={`lab-result-${result.id}-${index}`}>
                        <ListItem>
                          <ListItemText
                            primary={result.patient?.name || 'Pacient necunoscut'}
                            secondary={
                              <Box>
                                <Typography variant="body2">
                                  {result.name} {result.flagged && `(${result.flagged})`}
                                </Typography>
                                <Typography variant="caption" color="text.secondary">
                                  {formatForDisplay(result.received_at)}
                                </Typography>
                              </Box>
                            }
                          />
                          {result.flagged && (
                            <Chip
                              label={result.flagged === 'C' ? 'CRITIC' : result.flagged}
                              color={result.flagged === 'C' ? 'error' : 'warning'}
                              size="small"
                            />
                          )}
                        </ListItem>
                        {index < dashboardData.recent_lab_results.length - 1 && <Divider />}
                      </React.Fragment>
                    ))}
                  </List>
                )}
                <Box textAlign="center" pt={2}>
                  <Button variant="outlined" onClick={() => navigate('/doctor/lab')}>
                    Vezi toate rezultatele
                  </Button>
                </Box>
              </CardContent>
            </Card>
          </Stack>
        </Grid>

        {/* Right Column */}
        <Grid size={{ xs: 12, lg: 4 }}>
          <Stack spacing={3}>
            {/* Task-uri */}
            <Card>
              <CardHeader 
                title="Task-uri" 
                action={
                  <Badge badgeContent={dashboardData?.tasks_due?.filter((t: any) => t.status !== 'done').length || 0} color="error">
                    <TaskIcon />
                  </Badge>
                }
              />
              <CardContent>
                {!dashboardData?.tasks_due || dashboardData.tasks_due.length === 0 ? (
                  <Alert severity="success">
                    Nu exista task-uri in asteptare.
                  </Alert>
                ) : (
                  <Stack spacing={2}>
                    {dashboardData.tasks_due.slice(0, 5).map((task: any, index: number) => (
                      <Paper key={`task-${task.id}-${index}`} sx={{ p: 2 }}>
                        <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                          <Typography variant="subtitle2">
                            {task.title}
                          </Typography>
                          <Chip
                            label={task.priority}
                            color={getPriorityColor(task.priority)}
                            size="small"
                          />
                        </Box>
                        <Typography variant="body2" color="text.secondary">
                          {task.body || 'Fara descriere'}
                        </Typography>
                        {task.due_at && (
                          <Typography variant="caption" color="text.secondary">
                            Termen: {formatDateOnly(task.due_at)}
                          </Typography>
                        )}
                      </Paper>
                    ))}
                  </Stack>
                )}
                <Box textAlign="center" pt={2}>
                  <Button variant="outlined" onClick={() => navigate('/doctor/tasks')}>
                    Vezi toate task-urile
                  </Button>
                </Box>
              </CardContent>
            </Card>

            {/* Sesiuni Telemedicina */}
            {dashboardData?.telemed_sessions && dashboardData.telemed_sessions.length > 0 && (
              <Card>
                <CardHeader 
                  title="Telemedicina Astazi" 
                  avatar={<VideoIcon color="primary" />}
                />
                <CardContent>
                  <List>
                    {dashboardData.telemed_sessions.map((session: any, index: number) => (
                      <ListItem key={`telemed-${session.id}-${index}`}>
                        <ListItemText
                          primary={session.patient?.name || session.owner?.firstName}
                          secondary={new Date(session.scheduled_at).toLocaleTimeString('ro-RO', { hour: '2-digit', minute: '2-digit' })}
                        />
                        <Chip
                          label={session.status}
                          color={session.status === 'waiting' ? 'warning' : 'info'}
                          size="small"
                        />
                      </ListItem>
                    ))}
                  </List>
                  <Box textAlign="center" pt={2}>
                    <Button variant="contained" color="primary" onClick={() => navigate('/doctor/telemed')}>
                      Acceseaza Telemedicina
                    </Button>
                  </Box>
                </CardContent>
              </Card>
            )}

            {/* Pacienti recenti */}
            <Card>
              <CardHeader 
                title="Pacienti Recenti" 
                avatar={<PetsIcon color="primary" />}
              />
              <CardContent>
                {!dashboardData?.recent_patients || dashboardData.recent_patients.length === 0 ? (
                  <Alert severity="info">
                    Nu exista consultatii recente.
                  </Alert>
                ) : (
                  <List>
                    {dashboardData.recent_patients.map((patient: any, index: number) => (
                      <ListItem 
                        key={`patient-${patient.id}-${index}`}
                        component="div"
                        sx={{ cursor: 'pointer', '&:hover': { bgcolor: 'action.hover' } }}
                        onClick={() => navigate(`/doctor/patients/${patient.id}`)}
                      >
                        <ListItemAvatar>
                          <Avatar>
                            <PetsIcon />
                          </Avatar>
                        </ListItemAvatar>
                        <ListItemText
                          primary={patient.name}
                          secondary={
                            <>
                              {patient.visit_type} • {formatDateOnly(patient.visit_date)}
                            </>
                          }
                        />
                      </ListItem>
                    ))}
                  </List>
                )}
              </CardContent>
            </Card>
          </Stack>
        </Grid>
      </Grid>
    </Box>
  );
};

export default DoctorDashboard;

